import React, { MouseEvent, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Logout from '@mui/icons-material/Logout';
import SyncLockOutlinedIcon from '@mui/icons-material/SyncLockOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HomeIcon from '@mui/icons-material/Home';
import {
  getInitialLetterOfname,
  isAdminRoute,
  isAdminUser,
  isMyAccountRoute,
} from '../../utilities/helper';
import { logout } from '../../store/slices/auth.slice';
import FormModal from '../modals/form.modal';
import { showSnackbar } from '../../store/slices/common.slice';
import ChangePasswordForm from '../forms/change-password.form';
import useNavigateHook from '../../hooks/navigate.hook';

const UserMenu: React.FC = () => {
  const dispatch = useDispatch();
  const { doNavigate } = useNavigateHook();
  const { pathname } = useLocation();
  const [showChangePasswordFormModal, setShowChangePasswordFormModal] = useState<boolean>(false);
  const { user } = useSelector((state: any) => state.auth);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const doLogout = () => {
    dispatch(logout());
    handleClose();
  };

  const redirectToMyAccount = (path: string) => {
    handleClose();
    doNavigate(`my-account/${path}`);
  };

  const onChangePasswordSuccess = useCallback(() => {
    setShowChangePasswordFormModal(false);
    dispatch(showSnackbar({ message: 'Password changed successfully', severity: 'success' }));
  }, [dispatch]);

  const showAdminMenu = useCallback(() => {
    let show = false;
    if (isAdminUser(user)) {
      if (!isMyAccountRoute(pathname)) {
        show = true;
      }
    }

    return show;
  }, [pathname, user]);
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
        <Tooltip title={user?.name || 'User Account'}>
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={open ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <Avatar sx={{ width: 32, height: 32, p: 0.5, fontSize: '16px' }}>
              {getInitialLetterOfname(user?.name)}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="user-menu"
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {showAdminMenu() ? (
          isAdminRoute(pathname) ? (
            <>
              <MenuItem onClick={() => doNavigate('/admin/my-account')}>
                <Avatar sx={{ fontSize: '24px' }} /> Profile
              </MenuItem>
              <MenuItem onClick={() => setShowChangePasswordFormModal(true)}>
                <ListItemIcon>
                  <SyncLockOutlinedIcon fontSize="small" />
                </ListItemIcon>
                Change Password
              </MenuItem>
              <MenuItem onClick={() => doNavigate('/')}>
                <ListItemIcon>
                  <HomeIcon fontSize="small" />
                </ListItemIcon>
                Go To Website
              </MenuItem>
            </>
          ) : (
            <MenuItem onClick={() => doNavigate('/admin/dashboard')}>
              <ListItemIcon>
                <DashboardIcon fontSize="small" />
              </ListItemIcon>
              Admin Portal
            </MenuItem>
          )
        ) : (
          <>
            <MenuItem onClick={() => redirectToMyAccount('profile')}>
              <Avatar sx={{ fontSize: '24px' }} /> Profile
            </MenuItem>
            <MenuItem onClick={() => setShowChangePasswordFormModal(true)}>
              <ListItemIcon>
                <SyncLockOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Change Password
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => redirectToMyAccount('auctions')}>
              <ListItemIcon>
                <CategoryOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Auctions
            </MenuItem>
          </>
        )}

        <MenuItem onClick={doLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
      {showChangePasswordFormModal && (
        <FormModal
          open={showChangePasswordFormModal}
          onClose={() => setShowChangePasswordFormModal(false)}
          size="xs"
        >
          <ChangePasswordForm onSuccess={onChangePasswordSuccess} />
        </FormModal>
      )}
    </>
  );
};

export default UserMenu;
